<template>
<div class="container-fluid p-0">
  <div v-if="currentDelivery">
    <div class="d-flex gap-3 align-items-center p-2">
      <RouterLink :to="TranslationsService.i18nRoute({ name: 'deliveries' })" class="btn btn-outline-secondary d-inline-block" active-class="">
        <font-awesome-icon :icon="['fas', 'chevron-left']" />
        {{ $t('global.list') }}
      </RouterLink>
      <div>
        <span class="text-primary fw-bold fs-3">{{ currentDelivery.code }}</span><br>
        <span class="text-melodi">{{ currentDelivery.description }}</span><br>
      </div>
    </div>
    <div v-if="selectedSiteId === currentDelivery.site.id">
      <div :disabled="isSaving" class="input-group">
        <button class="btn btn-primary" @click="submitForm" v-if="currentDelivery.status == DELIVERY_HEAD_STATUS.inProgress">
          <font-awesome-icon :icon="['fas', 'check']" />
          {{ $t('page.delivery.form.book') }}
        </button>
        <button class="btn btn-outline-primary d-none d-md-inline" @click="print" v-if="hasRequiredLabels">
          <font-awesome-icon :icon="['fas', 'print-magnifying-glass']" />
          {{ $t('action.printPreviewEllipsis') }}
        </button>
        <DeliveryAttachmentList :delivery-id="currentDelivery.id"/>
        <span class="input-group-text d-none d-md-inline" v-if="!hasRequiredLabels">{{ $t('error.noLabelsToPrint') }}</span>
      </div>
    </div>
    <div v-else class="alert alert-warning" role="alert">
      <div class="input-group">
        <DeliveryAttachmentList :delivery-id="currentDelivery.id"/>
        <span>{{ $t('page.delivery.differentSiteWarning') }}&colon; <sup>{{ currentDelivery.site.code }}</sup> {{ currentDelivery.site.name }}</span>
      </div>
    </div>

    <div class="d-flex">
      <div class="p-2 align-self-center">
        <DeliveryStatus :status="currentDelivery.status"/>
      </div>
      <div class="p-2">
        {{ $t('field.generic.created') }}<br>
        <AuditItem :at="currentDelivery.audit.createdAt" :by="currentDelivery.audit.createdBy" />
      </div>
      <div class="p-2" v-if="currentDelivery.audit.createdAt !== currentDelivery.audit.updatedAt">
        {{ $t('field.generic.updated') }}<br>
        <AuditItem :at="currentDelivery.audit.updatedAt" :by="currentDelivery.audit.updatedBy" />
      </div>
    </div>

    <DeliveryLineList :delivery-id="currentDelivery.id"/>
  </div>
  <div v-else>
    <span>{{ $t('error.noData') }}</span>
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDeliveriesStore, useSessionStore } from '@/stores';
import { toast, type ToastOptions } from 'vue3-toastify';
import TranslationsService from "@/services/translations.ts";
import DeliveryAttachmentList from '@/components/deliveries/DeliveryAttachmentList.vue';
import DeliveryLineList from "@/components/deliveries/DeliveryLineList.vue";
import { router } from "@/services";
import { useI18n } from 'vue-i18n';
import DeliveryStatus from '@/components/deliveries/DeliveryStatus.vue';
import AuditItem from '@/components/AuditItem.vue';
import { onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import Delivery from '@/models/frontend/Delivery';
import { DELIVERY_HEAD_STATUS } from '@/models/Enums';

const print = async () => {
  await router.push({ name: 'delivery-print', params: { deliveryId: props.deliveryId } });
};

const i18n = useI18n();
const sessionStore = useSessionStore();
const deliveriesStore = useDeliveriesStore();
const { error: deliveryStoreError } = storeToRefs(deliveriesStore);

const { selectedSiteId } = storeToRefs(sessionStore);

const props = defineProps({
  deliveryId: { type: Number, required: true }
});

const currentDelivery = ref<Delivery | undefined>(deliveriesStore.getById(props.deliveryId));

const isSaving = ref(false);

onMounted(async () => {
  if (currentDelivery?.value == undefined) {
    await deliveriesStore.fetch(props.deliveryId);
    currentDelivery.value = deliveriesStore.getById(props.deliveryId);
  }
  await deliveriesStore.fetchAttachments(props.deliveryId);
});

const hasRequiredLabels = computed(() => {
  return currentDelivery?.value?.lines?.some(line => line.item.requiresLabel && (line.item.trackLotNumber || line.item.trackBestBeforeDate || line.item.trackExpirationDate));
});

const submitForm = async () => {
  if (currentDelivery?.value == undefined) {
    toast.error(i18n.t('page.delivery.notFoundError'));
    return;
  }

  isSaving.value = true;

  await deliveriesStore.complete(currentDelivery.value.id);
  if (deliveryStoreError.value != '') {
    toast.error(i18n.t('page.delivery.completeError', { error: deliveryStoreError.value }), { theme: 'colored' });
    isSaving.value = false;
  }
  else {
    currentDelivery.value.status = DELIVERY_HEAD_STATUS.completed;
    toast.info(i18n.t('page.delivery.completed', { deliveryCode: currentDelivery.value.code }), { theme: 'colored', autoClose: 1500 } as ToastOptions);

    setTimeout(() => {
      router.push({ name: 'deliveries' });
      isSaving.value = false;
    }, 2000);
  }
};
</script>
